import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Cross, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianPictureLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter code, 2: Select pictures
  const [code, setCode] = useState('');
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [loading, setLoading] = useState(false);

  const pictureOptions = [
    { id: 'lion', name: 'Lion', emoji: '🦁' },
    { id: 'elephant', name: 'Elephant', emoji: '🐘' },
    { id: 'rainbow', name: 'Rainbow', emoji: '🌈' },
    { id: 'star', name: 'Star', emoji: '⭐' },
    { id: 'heart', name: 'Heart', emoji: '❤️' },
    { id: 'sun', name: 'Sun', emoji: '☀️' },
    { id: 'moon', name: 'Moon', emoji: '🌙' },
    { id: 'flower', name: 'Flower', emoji: '🌸' },
    { id: 'tree', name: 'Tree', emoji: '🌳' },
    { id: 'fish', name: 'Fish', emoji: '🐟' },
    { id: 'bird', name: 'Bird', emoji: '🐦' },
    { id: 'butterfly', name: 'Butterfly', emoji: '🦋' },
    { id: 'crown', name: 'Crown', emoji: '👑' },
    { id: 'cross', name: 'Cross', emoji: '✝️' },
    { id: 'dove', name: 'Dove', emoji: '🕊️' },
    { id: 'lamb', name: 'Lamb', emoji: '🐑' }
  ];

  const handleCodeSubmit = () => {
    if (code.length < 4) {
      toast.error('Please enter your code');
      return;
    }
    setStep(2);
  };

  const handlePictureSelect = (pictureId) => {
    if (selectedPictures.includes(pictureId)) {
      setSelectedPictures(prev => prev.filter(id => id !== pictureId));
    } else if (selectedPictures.length < 2) {
      setSelectedPictures(prev => [...prev, pictureId]);
    }
  };

  const handleLogin = async () => {
    if (selectedPictures.length !== 2) {
      toast.error('Select both pictures!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/christian/picture-login`, {
        child_code: code.toUpperCase(),
        selected_images: selectedPictures
      });

      if (response.data.success) {
        // Store token with correct key for Christian module
        localStorage.setItem('littlelearners-token', response.data.token);
        localStorage.setItem('token', response.data.token); // Backup for compatibility
        localStorage.setItem('christian_child', JSON.stringify(response.data.child));
        toast.success(`Welcome back, ${response.data.child.name}!`);
        navigate(`/christian/child/${response.data.child.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wrong pictures. Try again!');
      setSelectedPictures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedPictures([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 flex flex-col items-center justify-center p-4">
      {/* Back Link */}
      <div className="w-full max-w-md mb-6">
        <Link to="/christian" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>
      </div>

      {/* Logo */}
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center mb-8"
      >
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30">
          <Cross className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Kid Login</h1>
        <p className="text-purple-400">Pick your pictures to sign in!</p>
      </motion.div>

      {/* Step 1: Enter Code */}
      {step === 1 && (
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full max-w-md"
        >
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <p className="text-white text-center mb-4 text-lg">Enter your code</p>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Your code"
                maxLength={6}
                className="bg-slate-700 border-slate-600 text-white text-center text-3xl font-mono tracking-widest placeholder:text-slate-500 h-16"
              />
              <Button
                onClick={handleCodeSubmit}
                disabled={code.length < 4}
                className="w-full mt-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg py-6"
              >
                Next
              </Button>
            </CardContent>
          </Card>

          <p className="text-center text-slate-500 text-sm mt-4">
            Ask your parent for your login code
          </p>
        </motion.div>
      )}

      {/* Step 2: Select Pictures */}
      {step === 2 && (
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full max-w-md"
        >
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-white">Tap your 2 pictures!</p>
                <div className="flex items-center gap-2">
                  <span className="text-purple-400 font-bold">{selectedPictures.length}/2</span>
                  {selectedPictures.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="text-slate-400 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Selected Pictures */}
              <div className="flex items-center justify-center gap-3 mb-4">
                {[0, 1].map((i) => (
                  <div 
                    key={i} 
                    className={`w-16 h-16 rounded-xl flex items-center justify-center text-3xl transition-all ${
                      selectedPictures[i] 
                        ? 'bg-purple-500/30 ring-2 ring-purple-500' 
                        : 'bg-slate-700/50 border-2 border-dashed border-slate-600'
                    }`}
                  >
                    {selectedPictures[i] ? (
                      pictureOptions.find(p => p.id === selectedPictures[i])?.emoji
                    ) : (
                      <span className="text-slate-500 text-sm">{i + 1}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Picture Grid */}
              <div className="grid grid-cols-4 gap-2">
                {pictureOptions.map((option, idx) => (
                  <motion.button
                    key={option.id}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    onClick={() => handlePictureSelect(option.id)}
                    disabled={selectedPictures.length >= 2 && !selectedPictures.includes(option.id)}
                    className={`aspect-square rounded-2xl flex flex-col items-center justify-center transition-all text-4xl ${
                      selectedPictures.includes(option.id)
                        ? 'bg-purple-500/30 ring-2 ring-purple-500 scale-90'
                        : 'bg-slate-700/50 hover:bg-slate-700 active:scale-95'
                    } ${
                      selectedPictures.length >= 2 && !selectedPictures.includes(option.id)
                        ? 'opacity-50'
                        : ''
                    }`}
                  >
                    {option.emoji}
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => { setStep(1); setSelectedPictures([]); }}
              className="flex-1 border-slate-700 text-white hover:bg-slate-800"
            >
              Back
            </Button>
            <Button
              onClick={handleLogin}
              disabled={loading || selectedPictures.length !== 2}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Go!'
              )}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ChristianPictureLogin;
